// Shared eSIMGO search helpers for snippets/bundle-search.liquid and snippets/popup-search.liquid.
// Config comes from window.routes / window.esimgoSearch (set in layout/theme.liquid) unless a caller passes overrides.
(function () {
  if (window.esimgoSearchHelpers) return;

  function config(overrides = {}) {
    return {
      searchUrl: overrides.searchUrl || window.routes?.esimgo_search_url,
      lang: overrides.lang || window.esimgoSearch?.currentLang || 'en',
    };
  }

  // Resolves to the search API response; rejects on a network/HTTP error or an aborted signal.
  async function search(query, { signal, limit = 10, ...overrides } = {}) {
    const { searchUrl, lang } = config(overrides);
    const response = await fetch(`${searchUrl}?q=${encodeURIComponent(query)}&lang=${lang}&limit=${limit}`, {
      signal,
    });
    if (!response.ok) throw new Error('Search failed');
    return response.json();
  }

  // clickPath is like "/search?action=click&eventId=xxx&handle=yyy" and goes through the app proxy for analytics
  function trackingUrl(item, overrides) {
    const { searchUrl } = config(overrides);
    return item.clickPath ? searchUrl.replace('/search', '') + item.clickPath : item.url;
  }

  // Displayed country count for a result, matching snippets/product-country-count.liquid: the unique
  // ISO codes the bundle covers (item.countryCount), plus the Travel Pass inflate setting.
  function countryCount(item) {
    const count = item.countryCount || 0;
    if (!count || !/travel pass/i.test(item.title || '')) return count;
    return count + (window.esimgoSearch?.travelPassCountryAdd || 0);
  }

  function itemHandle(item) {
    if (item.handle) return item.handle;
    const match = String(item.url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function formatMoneyCents(cents, lang) {
    const currency = window.Shopify?.currency?.active || window.esimgoSearch?.currency || 'GBP';
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  // item.price is quoted in the shop's base currency, and multiplying it by Shopify.currency.rate
  // doesn't match market prices (price lists, fixed prices and rounding rules all diverge). Instead
  // fetch the product's real presentment price for the visitor's market, cached per handle.
  const priceCache = new Map();

  // Resolves to a formatted "from" price for a search result, or null when it has none.
  function fetchPrice(item, overrides) {
    const handle = item.price ? itemHandle(item) : null;
    if (!handle) return Promise.resolve(null);

    if (!priceCache.has(handle)) {
      const { lang } = config(overrides);
      const root = window.Shopify?.routes?.root || '/';
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((product) => (typeof product.price_min === 'number' ? formatMoneyCents(product.price_min, lang) : null))
        .catch(() => {
          priceCache.delete(handle);
          return null;
        });
      priceCache.set(handle, request);
    }
    return priceCache.get(handle);
  }

  // Legacy local search: matches against every product fetched from the Storefront API, used when the
  // eSIMGO search API is disabled or fails. The product list is cached in sessionStorage for the visit.
  let legacyProductsRequest = null;

  // Legacy data is cached under this key; bump it when the cached shape changes
  const LEGACY_CACHE_KEY = 'countries_v2';

  async function storefrontQuery(query, variables) {
    // apiSettings (shop domain + Storefront token) is a global set in layout/theme.liquid
    const { domain, token } = typeof apiSettings !== 'undefined' ? apiSettings : {};
    const response = await fetch(`${domain}/api/2025-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) throw new Error('Storefront request failed');
    const result = await response.json();
    if (!result.data) throw new Error(result.errors?.[0]?.message || 'Storefront request failed');
    return result.data;
  }

  // Aliases by upper-case ISO code, from the country_alias metaobjects the search API also uses. Each
  // alias is a plain string or { value, translations: [{ language, value }] }; every spelling counts.
  // Needs Storefront access on the metaobject definition, otherwise legacy search runs without aliases.
  async function fetchLegacyAliases() {
    const query = `
      query CountryAliases($after: String) {
        metaobjects(type: "country_alias", first: 250, after: $after) {
          nodes {
            fields {
              key
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;
    const aliasesByIso = {};
    let after = null;
    do {
      const { metaobjects } = await storefrontQuery(query, { after });
      for (const node of metaobjects.nodes) {
        const field = (key) => node.fields.find((f) => f.key === key)?.value;
        const iso = field('iso_code');
        if (!iso) continue;
        let parsed = [];
        try {
          parsed = JSON.parse(field('aliases') || '[]');
        } catch (e) {
          // Invalid JSON, skip
        }
        const spellings = [];
        for (const alias of Array.isArray(parsed) ? parsed : []) {
          if (typeof alias === 'string') {
            spellings.push(alias);
          } else if (alias?.value) {
            spellings.push(alias.value, ...(alias.translations || []).map((t) => t.value));
          }
        }
        aliasesByIso[iso.toUpperCase()] = spellings.filter(Boolean);
      }
      after = metaobjects.pageInfo.hasNextPage ? metaobjects.pageInfo.endCursor : null;
    } while (after);
    return aliasesByIso;
  }

  const ALIASES_CACHE_KEY = 'country_aliases_v1';
  let aliasesRequest = null;

  // Resolves to the aliases by ISO (see fetchLegacyAliases), fetched once per visit. Also used by the
  // CoverageModal island's country search. Rejects when they can't be fetched, so the next call retries.
  function countryAliases() {
    try {
      const stored = sessionStorage.getItem(ALIASES_CACHE_KEY);
      if (stored) return Promise.resolve(JSON.parse(stored));
    } catch (e) {
      // Unreadable cache: fall through and refetch
    }

    if (!aliasesRequest) {
      aliasesRequest = fetchLegacyAliases()
        .then((aliasesByIso) => {
          try {
            sessionStorage.setItem(ALIASES_CACHE_KEY, JSON.stringify(aliasesByIso));
          } catch (e) {
            // Storage full or blocked: keep the in-memory copy only
          }
          return aliasesByIso;
        })
        .catch((error) => {
          aliasesRequest = null;
          throw error;
        });
    }
    return aliasesRequest;
  }

  async function fetchLegacyProducts() {
    const query = `
      {
        products(first: 250) {
          edges {
            node {
              handle
              title
              metafields(
                identifiers: [{namespace: "esimgo", key: "countries"}, {namespace: "esimgo", key: "roamingEnabled"}, {namespace: "esimgo", key: "coverage_map"}]
              ) {
                key
                value
                reference {
                  ... on MediaImage {
                    image {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    let aliasesLoaded = true;
    const [data, aliasesByIso] = await Promise.all([
      storefrontQuery(query),
      countryAliases().catch((error) => {
        console.warn('Legacy search aliases unavailable:', error);
        aliasesLoaded = false;
        return {};
      }),
    ]);

    const products = data.products.edges.map(({ node: product }) => {
      const metafields = {};
      product.metafields.forEach((mf) => {
        if (mf?.key) metafields[mf.key] = { value: mf.value, imageUrl: mf.reference?.image?.url || null };
      });
      const countries = metafields.countries?.value ? JSON.parse(metafields.countries.value) : [];
      for (const entry of countries) {
        const iso = entry?.country?.iso?.toUpperCase();
        if (iso && aliasesByIso[iso]) entry.country.aliases = aliasesByIso[iso];
      }
      return {
        name: product.title,
        handle: product.handle,
        countries,
        roaming: metafields.roamingEnabled?.value ? JSON.parse(metafields.roamingEnabled.value) : [],
        coverage: metafields.coverage_map?.imageUrl || '',
      };
    });
    return { products, cacheable: aliasesLoaded };
  }

  // Resolves to the cached product list, fetching it once per visit.
  function legacyProducts() {
    try {
      const stored = sessionStorage.getItem(LEGACY_CACHE_KEY);
      if (stored) return Promise.resolve(JSON.parse(stored));
    } catch (e) {
      // Unreadable cache: fall through and refetch
    }

    if (!legacyProductsRequest) {
      legacyProductsRequest = fetchLegacyProducts()
        .then(({ products, cacheable }) => {
          // Without aliases, keep the list for this page only so the next page load retries them
          if (cacheable) {
            try {
              sessionStorage.setItem(LEGACY_CACHE_KEY, JSON.stringify(products));
            } catch (e) {
              // Storage full or blocked: keep the in-memory copy only
            }
          }
          return products;
        })
        .catch((error) => {
          legacyProductsRequest = null;
          throw error;
        });
    }
    return legacyProductsRequest;
  }

  // Same count as snippets/product-country-count.liquid: unique 2-3 character ISO codes across the
  // countries and roaming lists, skipping the bundle placeholder entry (e.g. iso "Europe+")
  function uniqueIsoCount(product) {
    const isos = new Set();
    for (const entry of [...product.countries, ...product.roaming]) {
      const iso = entry?.country?.iso;
      if (iso && iso.length >= 2 && iso.length <= 3) isos.add(iso.toUpperCase());
    }
    return isos.size;
  }

  function displayName(country) {
    return country.iso && country.iso.length == 2 && typeof getDisplayName === 'function'
      ? getDisplayName(country.iso)
      : country.name;
  }

  // Resolves to matching items, name matches before alias matches. Each item has the search result
  // fields (title, handle, url, flag, countryCount) plus the matched `country`, its display `name`,
  // `isCoverage` (flag is a coverage map), `isAlias` and `exact` (the query equals the name).
  async function legacySearch(query) {
    const products = await legacyProducts();
    const term = query.toLowerCase();
    const root = window.Shopify?.routes?.root || '/';
    const nameMatches = [];
    const aliasMatches = [];

    for (const product of products) {
      // Regional products match on the product itself, single-country products on their country
      const isRegional = product.roaming.length > product.countries.length;
      const country = isRegional ? product : product.countries[0]?.country;
      if (!country) continue;

      const name = displayName(country);
      let matchedAlias = null;
      if (term.length >= 2 && !country.name.toLowerCase().includes(term)) {
        for (const alias of country.aliases || []) {
          if (alias && alias.toLowerCase().includes(term) && (!matchedAlias || matchedAlias.length > alias.length)) {
            matchedAlias = alias;
          }
        }
      }
      if (!matchedAlias && !name.toLowerCase().includes(term) && !country.name.toLowerCase().includes(term)) continue;

      const item = {
        title: matchedAlias ? `${matchedAlias} (${name})` : name,
        name,
        country,
        handle: product.handle,
        url: `${root}products/${product.handle}`,
        // A coverage map shows for regional products, where the matched country is the product itself
        flag: country.coverage || (country.iso ? `https://cdn.breezesim.com/flag/1x1/${country.iso.toLowerCase()}.svg` : null),
        isCoverage: Boolean(country.coverage),
        countryCount: isRegional ? uniqueIsoCount(product) : 1,
        isAlias: Boolean(matchedAlias),
        exact: name.toLowerCase() === term || country.name.toLowerCase() === term,
      };
      (matchedAlias ? aliasMatches : nameMatches).push(item);
    }

    return nameMatches.concat(aliasMatches);
  }

  window.esimgoSearchHelpers = { search, trackingUrl, fetchPrice, countryCount, legacyProducts, legacySearch, countryAliases };
})();
